/**
 * The statuses a quote request moves through, in the order it moves through
 * them.
 *
 * Shared by the dashboard tiles and the Quotes list filters so the two cannot
 * drift: every count on the dashboard has a filter behind it, and a tile that
 * says "3 drafting" opens a list holding those three.
 *
 * The `status` column is a free-form string with a default of `pending` from
 * before this vocabulary settled, so rows predating it still carry words
 * nothing writes any more. They are mapped on read rather than migrated — a
 * count is a display concern, and rewriting history to tidy a dashboard is a
 * poor trade.
 */
export const QUOTE_REQUEST_STATUSES = ['new', 'drafting', 'awaiting response', 'accepted'];

const LEGACY_STATUS_ALIASES = {
  pending: 'new',
  reviewed: 'drafting',
  quoted: 'awaiting response',
  completed: 'accepted'
};

/**
 * Map whatever is on the row onto one of QUOTE_REQUEST_STATUSES.
 *
 * Anything unrecognised — including empty — reads as `new`: a request whose
 * state nobody can account for is one somebody still has to look at.
 */
export function normaliseQuoteRequestStatus(status) {
  const value = String(status || '').trim().toLowerCase();
  const mapped = LEGACY_STATUS_ALIASES[value] || value;
  return QUOTE_REQUEST_STATUSES.includes(mapped) ? mapped : 'new';
}

/**
 * Every value a row might hold for this status, canonical name first.
 *
 * A filter has to match what the counting matched, or a tile reading "3 new"
 * opens a list of two. Appwrite's `equal` takes an array and ORs it, so this
 * goes straight into the query.
 */
export function statusFilterValues(status) {
  const canonical = normaliseQuoteRequestStatus(status);
  const aliases = Object.keys(LEGACY_STATUS_ALIASES).filter(
    (alias) => LEGACY_STATUS_ALIASES[alias] === canonical
  );
  return [canonical, ...aliases];
}

/** An empty count per status, in pipeline order. */
export function emptyStatusCounts() {
  return Object.fromEntries(QUOTE_REQUEST_STATUSES.map((status) => [status, 0]));
}

/** Count quote requests by normalised status. */
export function countByStatus(quoteRequests = []) {
  const counts = emptyStatusCounts();
  quoteRequests.forEach((request) => {
    counts[normaliseQuoteRequestStatus(request?.status)] += 1;
  });
  return counts;
}
